/**
 * Wall 3D Viewer Component
 *
 * Shows a single wall with board-level product rendering.
 * Uses the same shared modules as Room3DViewer for consistency.
 * Default view: straight-on (looking directly at the wall face).
 */

import { useTranslation } from 'react-i18next';
import React, { useEffect, useRef, useMemo, useCallback } from 'react';
import * as THREE from 'three';
import { RotateCcw, Maximize, Minimize } from 'lucide-react';

import { createScene, addLighting, addViewerLighting, configureRenderer } from './sceneBuilder';
import { registerLight, unregisterLight, LightingRegistryEntry } from '../../dev/lightingRegistry';
import { createBoardMaterial } from './materialFactory';
import { generateWallBoardLayout } from './boardLayoutEngine';
import { BoardRect, GarageConfigProp } from './types';
import { createGarageDoorGroupForWall } from './garageDoorBuilder';
import { formatDimension } from './dimensionLabels';
import {
  DEFAULT_FOV,
  applyDragRotation,
  applyZoom,
  interpolateState,
  calculateZoomToFit,
} from './cameraController';
import { attachTouchControls } from './touchController';
import { exitFullscreenSafe, fullscreenSupported, requestFullscreenSafe } from '../../../utils/fullscreen';

import '../Room3DViewer.css';

interface Wall3DViewerProps {
  wallLength: number;
  wallHeight: number;
  wallColor: 'White' | 'Gray';
  orientation: 'Horizontal' | 'Vertical';
  wallName?: string;
  slatWallConfig?: {
    enabled: boolean;
    startHeight: number;
    numPanelRows: number;
    color: 'White' | 'Gray';
  } | null;
  garageConfig?: GarageConfigProp | null;
}

// Straight-on: looking directly at the wall face (no rotation)
const WALL_INITIAL_ROTATION = { x: 0, y: 0 };

export const Wall3DViewer: React.FC<Wall3DViewerProps> = ({
  wallLength,
  wallHeight,
  wallColor,
  orientation,
  wallName = 'Wall',
  slatWallConfig = null,
  garageConfig = null,
}) => {
  const { t } = useTranslation('common');
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const groupRef = useRef<THREE.Group | null>(null);
  const rotationRef = useRef({ ...WALL_INITIAL_ROTATION });
  const orbitDistanceRef = useRef(20);
  const centerRef = useRef(new THREE.Vector3(0, 0, 0));
  const isDraggingRef = useRef(false);
  const previousMouseRef = useRef({ x: 0, y: 0 });
  const animationFrameRef = useRef<number | null>(null);
  const spinnerRef = useRef<HTMLDivElement>(null);
  const lightingEntryRef = useRef<LightingRegistryEntry | null>(null);

  const [isAnimating, setIsAnimating] = React.useState(false);
  const [sceneReady, setSceneReady] = React.useState(false);
  const [hasRotated, setHasRotated] = React.useState(false);
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const viewerWrapperRef = useRef<HTMLDivElement>(null);

  // iPhone Safari has no Element.requestFullscreen — the guarded helpers never
  // call what isn't there, and the button is hidden.
  const canFullscreen = fullscreenSupported();
  const toggleFullscreen = useCallback(() => {
    if (!viewerWrapperRef.current) return;
    if (!document.fullscreenElement) {
      void requestFullscreenSafe(viewerWrapperRef.current).then((ok) => { if (ok) setIsFullscreen(true); });
    } else {
      void exitFullscreenSafe().then((ok) => { if (ok) setIsFullscreen(false); });
    }
  }, []);

  useEffect(() => {
    const handler = () => {
      const fs = !!document.fullscreenElement;
      setIsFullscreen(fs);
      setTimeout(() => {
        const container = containerRef.current;
        if (container && cameraRef.current && rendererRef.current) {
          cameraRef.current.aspect = container.clientWidth / container.clientHeight;
          cameraRef.current.updateProjectionMatrix();
          rendererRef.current.setSize(container.clientWidth, container.clientHeight);
          renderScene();
        }
      }, 100);
    };
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const boardLayout = useMemo(() => {
    return generateWallBoardLayout(
      'single-wall', wallLength, wallHeight, wallColor, orientation, slatWallConfig
    );
  }, [wallLength, wallHeight, wallColor, orientation, slatWallConfig]);

  const renderScene = () => {
    if (!cameraRef.current || !rendererRef.current || !sceneRef.current) return;
    const r = rotationRef.current;
    const d = orbitDistanceRef.current;
    const c = centerRef.current;
    cameraRef.current.position.set(
      d * Math.sin(r.y) * Math.cos(r.x) + c.x,
      d * Math.sin(r.x) + c.y,
      d * Math.cos(r.y) * Math.cos(r.x) + c.z
    );
    cameraRef.current.lookAt(c);
    cameraRef.current.updateProjectionMatrix();
    rendererRef.current.render(sceneRef.current, cameraRef.current);
  };

  useEffect(() => {
    if (!containerRef.current) return;

    // Instantly show spinner before any scene work
    if (spinnerRef.current) {
      spinnerRef.current.style.transition = 'none';
      spinnerRef.current.style.opacity = '1';
      spinnerRef.current.style.pointerEvents = 'auto';
    }
    setSceneReady(false);

    const container = containerRef.current;

    const scene = createScene();
    sceneRef.current = scene;
    addLighting(scene);

    const aspect = container.clientWidth / container.clientHeight;
    const camera = new THREE.PerspectiveCamera(50, aspect, 0.1, 500);
    centerRef.current.set(wallLength / 2, wallHeight / 2, 0);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    configureRenderer(renderer, true);
    renderer.setSize(container.clientWidth, container.clientHeight);
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const group = new THREE.Group();
    groupRef.current = group;
    scene.add(group);
    buildWall(group);

    // Add interior lighting + envMap (wall mode: imaginary square room)
    const readyTimeout = setTimeout(() => {
      renderScene();
      setSceneReady(true);
    }, 2000);

    let hemiLight: THREE.HemisphereLight | null = null;
    scene.traverse(child => {
      if (child instanceof THREE.HemisphereLight) hemiLight = child;
    });

    const interiorLight = addViewerLighting(scene, group, renderer, 'wall',
      { length: wallLength, width: wallLength, height: wallHeight },
      (light, setup) => {
        clearTimeout(readyTimeout);
        const entry: LightingRegistryEntry = {
          viewerType: 'wall',
          light,
          hemiLight,
          probe: setup.probe,
          envMap: setup.envMap,
          floorMesh: null,
          renderer,
          scene,
          roomGroup: group,
          renderScene,
          getRotation: () => ({ ...rotationRef.current }),
          roomDimensions: { length: wallLength, width: wallLength, height: wallHeight },
        };
        lightingEntryRef.current = entry;
        registerLight(entry);
        requestAnimationFrame(() => {
          renderScene();
          setSceneReady(true);
        });
      }
    );

    // Zoom to fit
    const fitDist = calculateZoomToFit(camera, group, rotationRef.current, centerRef.current);
    orbitDistanceRef.current = fitDist;

    renderScene();

    const handleResize = () => {
      if (!container || !cameraRef.current || !rendererRef.current) return;
      cameraRef.current.aspect = container.clientWidth / container.clientHeight;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(container.clientWidth, container.clientHeight);
      renderScene();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(readyTimeout);
      if (lightingEntryRef.current) {
        unregisterLight(lightingEntryRef.current);
        lightingEntryRef.current = null;
      }
      window.removeEventListener('resize', handleResize);
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      if (container && rendererRef.current) container.removeChild(rendererRef.current.domElement);
      rendererRef.current?.dispose();
    };
  }, [wallLength, wallHeight, wallColor, orientation, boardLayout, garageConfig]);

  // Wheel zoom + touch controls
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const wheelHandler = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      if (!cameraRef.current) return;
      cameraRef.current.fov = applyZoom(cameraRef.current.fov, e.deltaY);
      cameraRef.current.updateProjectionMatrix();
      renderScene();
    };
    container.addEventListener('wheel', wheelHandler, { passive: false });

    const detachTouch = attachTouchControls({
      container,
      getRotation: () => rotationRef.current,
      setRotation: (r) => { rotationRef.current = r; },
      getFov: () => cameraRef.current?.fov ?? 50,
      setFov: (fov) => {
        if (cameraRef.current) {
          cameraRef.current.fov = fov;
          cameraRef.current.updateProjectionMatrix();
        }
      },
      onUpdate: () => renderScene(),
      onDragStart: () => setHasRotated(true),
    });

    return () => {
      container.removeEventListener('wheel', wheelHandler);
      detachTouch();
    };
  }, []);

  const buildWall = (group: THREE.Group) => {
    group.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry?.dispose();
        if (child.material instanceof THREE.Material) child.material.dispose();
      }
    });
    while (group.children.length > 0) group.remove(group.children[0]);

    // Collect all boards
    const allBoards: BoardRect[] = [];
    for (const section of boardLayout.sections) allBoards.push(...section.boards);

    // Group by color and create merged meshes (wall is on Z=0 plane, facing +Z)
    const boardsByColor = new Map<string, BoardRect[]>();
    for (const b of allBoards) {
      if (!boardsByColor.has(b.color)) boardsByColor.set(b.color, []);
      boardsByColor.get(b.color)!.push(b);
    }

    for (const [colorHex, boards] of boardsByColor) {
      const isWhite = colorHex.toUpperCase() === '#FFFFFF';
      const material = createBoardMaterial(isWhite ? 'White' : 'Gray');
      const n = boards.length;
      const positions = new Float32Array(n * 4 * 3);
      const normals = new Float32Array(n * 4 * 3);
      const indices: number[] = [];

      for (let i = 0; i < n; i++) {
        const b = boards[i];
        const off = i * 12;
        const vo = i * 4;
        positions[off] = b.x;         positions[off+1] = b.y;          positions[off+2] = 0;
        positions[off+3] = b.x+b.width; positions[off+4] = b.y;          positions[off+5] = 0;
        positions[off+6] = b.x+b.width; positions[off+7] = b.y+b.height; positions[off+8] = 0;
        positions[off+9] = b.x;         positions[off+10] = b.y+b.height; positions[off+11] = 0;
        for (let v = 0; v < 4; v++) {
          normals[off+v*3] = 0; normals[off+v*3+1] = 0; normals[off+v*3+2] = 1;
        }
        indices.push(vo, vo+1, vo+2, vo, vo+2, vo+3);
      }

      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geo.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
      geo.setIndex(indices);
      group.add(new THREE.Mesh(geo, material));
    }

    // W&CB seam lines — on both sides of the wall (+Z and -Z)
    const wcbBoards = allBoards.filter(b => b.productType === 'WCB');
    const seamPoints: number[] = [];
    for (const zOff of [0.001, -0.001]) {
      for (const b of wcbBoards) {
        seamPoints.push(b.x, b.y, zOff, b.x+b.width, b.y, zOff);
        seamPoints.push(b.x+b.width, b.y, zOff, b.x+b.width, b.y+b.height, zOff);
        seamPoints.push(b.x+b.width, b.y+b.height, zOff, b.x, b.y+b.height, zOff);
        seamPoints.push(b.x, b.y+b.height, zOff, b.x, b.y, zOff);
      }
    }
    if (seamPoints.length > 0) {
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.Float32BufferAttribute(seamPoints, 3));
      group.add(new THREE.LineSegments(geo, new THREE.LineBasicMaterial({
        color: 0x666666, transparent: true, opacity: 0.5,
      })));
    }

    // SlatWall groove lines — on both sides
    const slatBoards = allBoards.filter(b => b.productType === 'SlatWall');
    if (slatBoards.length > 0) {
      const groovePoints: number[] = [];
      const LINE_SPACING = 2 / 12;
      const SW_PANEL_LENGTH = 8;

      for (const zOff of [0.001, -0.001]) {
        for (const b of slatBoards) {
          // Horizontal grooves every 2"
          const numLines = Math.floor(b.height / LINE_SPACING);
          for (let g = 1; g <= numLines; g++) {
            const ly = b.y + g * LINE_SPACING;
            if (ly >= b.y + b.height - 0.001) continue;
            groovePoints.push(b.x, ly, zOff, b.x+b.width, ly, zOff);
          }
          // Vertical seams at 8'
          const numCols = Math.ceil(b.width / SW_PANEL_LENGTH);
          for (let c = 1; c < numCols; c++) {
            const sx = b.x + c * SW_PANEL_LENGTH;
            if (sx >= b.x + b.width - 0.001) continue;
            groovePoints.push(sx, b.y, zOff, sx, b.y+b.height, zOff);
          }
        }
      }
      if (groovePoints.length > 0) {
        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.Float32BufferAttribute(groovePoints, 3));
        group.add(new THREE.LineSegments(geo, new THREE.LineBasicMaterial({
          color: 0xaaaaaa, transparent: true, opacity: 0.35,
        })));
      }
    }

    // Dimension lines (same style as Room3DViewer)
    const LINE_COLOR = 0x0066cc;
    const lineMat = new THREE.LineBasicMaterial({ color: LINE_COLOR, linewidth: 2 });
    const CAP = 0.3;

    const addLine = (s: THREE.Vector3, e: THREE.Vector3) => {
      group.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints([s, e]), lineMat));
    };
    const addCap = (pt: THREE.Vector3, axis: 'x' | 'y' | 'z') => {
      const s = pt.clone(); const e = pt.clone();
      (s as any)[axis] -= CAP; (e as any)[axis] += CAP;
      addLine(s, e);
    };
    const addLabel = (text: string, x: number, y: number, z: number) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      canvas.width = 256; canvas.height = 128;
      ctx.fillStyle = '#0066cc';
      ctx.font = 'bold 48px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, 128, 64);
      const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: new THREE.CanvasTexture(canvas) }));
      sprite.position.set(x, y, z);
      sprite.scale.set(2, 1, 1);
      group.add(sprite);
    };

    // Width (horizontal) along bottom
    const wOff = -0.8;
    addLine(new THREE.Vector3(0, wOff, 0), new THREE.Vector3(wallLength, wOff, 0));
    addCap(new THREE.Vector3(0, wOff, 0), 'y');
    addCap(new THREE.Vector3(wallLength, wOff, 0), 'y');
    addLabel(formatDimension(wallLength), wallLength / 2, wOff - 1, 0);

    // Height (vertical) along left
    const hOff = -0.8;
    addLine(new THREE.Vector3(hOff, 0, 0), new THREE.Vector3(hOff, wallHeight, 0));
    addCap(new THREE.Vector3(hOff, 0, 0), 'x');
    addCap(new THREE.Vector3(hOff, wallHeight, 0), 'x');
    addLabel(formatDimension(wallHeight), hOff - 1, wallHeight / 2, 0);

    // Garage doors overlay
    if (garageConfig?.enabled && garageConfig.numDoors > 0) {
      const doorGroup = createGarageDoorGroupForWall(garageConfig, wallLength);
      group.add(doorGroup);
    }
  };

  const resetView = () => {
    if (isAnimating || !cameraRef.current) return;
    setIsAnimating(true);
    const start = { rotation: { ...rotationRef.current }, fov: cameraRef.current.fov };
    const target = { rotation: { ...WALL_INITIAL_ROTATION }, fov: DEFAULT_FOV };
    const duration = 600;
    const startTime = Date.now();
    const animate = () => {
      const progress = Math.min((Date.now() - startTime) / duration, 1);
      const state = interpolateState(start, target, progress);
      rotationRef.current = state.rotation;
      if (cameraRef.current) { cameraRef.current.fov = state.fov; cameraRef.current.updateProjectionMatrix(); }
      renderScene();
      if (progress < 1) { animationFrameRef.current = requestAnimationFrame(animate); }
      else { setIsAnimating(false); setHasRotated(false); animationFrameRef.current = null; }
    };
    animate();
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    isDraggingRef.current = true;
    previousMouseRef.current = { x: e.clientX, y: e.clientY };
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDraggingRef.current) return;
    rotationRef.current = applyDragRotation(
      rotationRef.current,
      e.clientX - previousMouseRef.current.x,
      e.clientY - previousMouseRef.current.y
    );
    previousMouseRef.current = { x: e.clientX, y: e.clientY };
    setHasRotated(true);
    renderScene();
  };
  const handleMouseUp = () => { isDraggingRef.current = false; };

  return (
    <div ref={viewerWrapperRef} className={`room-3d-viewer compact ${isFullscreen ? 'fullscreen' : ''}`}>
      <div className="viewer-dimension-card">
        {`${wallName}: ${formatDimension(wallLength)} W × ${formatDimension(wallHeight)} H`}
      </div>
      <div
        ref={containerRef}
        className="viewer-container"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div ref={spinnerRef} style={{
          position: 'absolute', inset: 0, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          background: '#f9fafb', zIndex: 2, borderRadius: '8px',
          opacity: sceneReady ? 0 : 1,
          pointerEvents: sceneReady ? 'none' : 'auto',
          transition: sceneReady ? 'opacity 0.5s ease-in-out' : 'none',
        }}>
          <div style={{
            width: '24px', height: '24px', border: '3px solid #e5e7eb',
            borderTopColor: '#6b7280', borderRadius: '50%',
            animation: 'viewer-spin 0.8s linear infinite',
          }} />
        </div>
      </div>
      <div className="viewer-hint">{t('viewer.hint')}{isFullscreen ? t('viewer.hintEsc') : ''}</div>
      <div className="viewer-controls">
        <button
          className="btn btn-secondary"
          onClick={resetView}
          disabled={!hasRotated || isAnimating}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px',
            fontSize: 'var(--font-size-xs)', fontWeight: 600,
            cursor: (hasRotated && !isAnimating) ? 'pointer' : 'not-allowed',
            opacity: (hasRotated && !isAnimating) ? 1 : 0.5,
          }}
        >
          <RotateCcw size={14} />
          {t('viewer.resetView')}
        </button>
        {canFullscreen && (
          <button
            className="btn btn-secondary"
            onClick={toggleFullscreen}
            title={isFullscreen ? t('viewer.exitFullScreen') : t('viewer.fullScreen')}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px',
              fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            {isFullscreen ? <Minimize size={14} /> : <Maximize size={14} />}
            {isFullscreen ? t('viewer.exitFullScreen') : t('viewer.fullScreen')}
          </button>
        )}
      </div>
    </div>
  );
};

export default Wall3DViewer;
